
import { useState, useEffect, useRef } from 'react';

interface DeliveryMapProps {
  deliveryId?: string;
  isDriver?: boolean;
  userType?: 'client' | 'restaurant' | 'commerce' | 'entreprise' | 'livreur' | 'artisan';
  pickupLocation?: {
    lat: number;
    lng: number;
    address: string;
  };
  deliveryLocation?: {
    lat: number;
    lng: number;
    address: string;
  };
  currentLocation?: {
    lat: number;
    lng: number;
  };
  orderDetails?: {
    clientName: string;
    driverName: string;
    orderType: string;
    estimatedPrice: string;
    priority: 'normal' | 'urgent' | 'express';
  };
  onLocationUpdate?: (location: { lat: number; lng: number }) => void;
}

export default function DeliveryMap({
  deliveryId = 'LIV-2024-001',
  isDriver = false,
  userType = 'client',
  pickupLocation = { lat: 5.3364, lng: -4.0267, address: "Abidjan, Côte d'Ivoire" },
  deliveryLocation = { lat: 5.3600, lng: -3.9583, address: "Cocody, Abidjan" },
  currentLocation = { lat: 5.3482, lng: -3.9921 },
  orderDetails = {
    clientName: 'Kouassi Marie',
    driverName: 'Jean Traoré',
    orderType: 'Colis Standard',
    estimatedPrice: '2,500 FCFA',
    priority: 'normal'
  },
  onLocationUpdate
}: DeliveryMapProps) {
  const [driverLocation, setDriverLocation] = useState(currentLocation);
  const [estimatedTime, setEstimatedTime] = useState(15);
  const [distance, setDistance] = useState(8.5);
  const [isTracking, setIsTracking] = useState(false);
  const [deliveryStatus, setDeliveryStatus] = useState<'collecte' | 'en_route' | 'arrive' | 'livre'>('en_route');
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [mapError, setMapError] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);

  // Simulation du mouvement du livreur
  useEffect(() => {
    if (isTracking) {
      const interval = setInterval(() => {
        setDriverLocation(prev => {
          const newLat = prev.lat + (Math.random() - 0.5) * 0.001;
          const newLng = prev.lng + (Math.random() - 0.5) * 0.001;
          const newLocation = { lat: newLat, lng: newLng };
          
          if (onLocationUpdate) {
            onLocationUpdate(newLocation);
          }
          
          return newLocation;
        });
        
        setEstimatedTime(prev => Math.max(1, prev - 0.5));
        setDistance(prev => Math.max(0.1, prev - 0.2));
      }, 3000);

      return () => clearInterval(interval);
    }
  }, [isTracking, onLocationUpdate]);

  // Initialiser la carte avec fallback
  useEffect(() => {
    const initializeMap = () => {
      if (!mapRef.current) return;

      try {
        // Créer une carte simple avec iframe Google Maps en fallback
        const mapContainer = mapRef.current;
        
        // Calculer le centre entre pickup et delivery
        const centerLat = (pickupLocation.lat + deliveryLocation.lat) / 2;
        const centerLng = (pickupLocation.lng + deliveryLocation.lng) / 2;
        
        // URL pour Google Maps embed
        const embedUrl = `https://www.google.com/maps/embed/v1/directions?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dOWTgkKSFRG-iI&origin=${pickupLocation.lat},${pickupLocation.lng}&destination=${deliveryLocation.lat},${deliveryLocation.lng}&mode=driving&language=fr&region=CI`;
        
        mapContainer.innerHTML = `
          <iframe
            src="${embedUrl}"
            width="100%"
            height="100%"
            style="border:0; border-radius: 8px;"
            allowfullscreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Carte de suivi ONAPILA">
          </iframe>
        `;
        
        setMapError(false);
      } catch (error) {
        console.error('Erreur lors de l\'initialisation de la carte:', error);
        setMapError(true);
        
        // Fallback avec carte statique
        if (mapRef.current) {
          mapRef.current.innerHTML = `
            <div class="h-full bg-gradient-to-br from-teal-100 to-blue-100 flex items-center justify-center">
              <div class="text-center p-6">
                <i class="ri-map-pin-line text-4xl text-teal-600 mb-4"></i>
                <h3 class="text-lg font-semibold text-gray-900 mb-2">Suivi de livraison</h3>
                <p class="text-gray-600 mb-4">De: ${pickupLocation.address}</p>
                <p class="text-gray-600 mb-4">Vers: ${deliveryLocation.address}</p>
                <div class="bg-white rounded-lg p-4 shadow-sm">
                  <div class="flex items-center justify-center space-x-4">
                    <div class="text-center">
                      <div class="w-3 h-3 bg-green-500 rounded-full mx-auto mb-1"></div>
                      <span class="text-xs text-gray-600">Départ</span>
                    </div>
                    <div class="flex-1 h-0.5 bg-blue-500 relative">
                      <div class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
                    </div>
                    <div class="text-center">
                      <div class="w-3 h-3 bg-red-500 rounded-full mx-auto mb-1"></div>
                      <span class="text-xs text-gray-600">Arrivée</span>
                    </div>
                  </div>
                  <div class="mt-3 text-center">
                    <span class="text-sm font-medium text-blue-600">${estimatedTime} min restantes</span>
                  </div>
                </div>
              </div>
            </div>
          `;
        }
      }
    };

    // Délai pour s'assurer que le DOM est prêt
    const timer = setTimeout(initializeMap, 100);
    return () => clearTimeout(timer);
  }, [pickupLocation, deliveryLocation, estimatedTime]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'collecte': return 'bg-yellow-500';
      case 'en_route': return 'bg-blue-500';
      case 'arrive': return 'bg-orange-500';
      case 'livre': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'collecte': return 'Collecte en cours';
      case 'en_route': return 'En route vers vous';
      case 'arrive': return 'Arrivé à destination';
      case 'livre': return 'Livraison terminée';
      default: return 'Statut inconnu';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800';
      case 'express': return 'bg-orange-100 text-orange-800';
      default: return 'bg-blue-100 text-blue-800';
    }
  };

  const getUserTypeLabel = (type: string) => {
    switch (type) {
      case 'restaurant': return 'Restaurant';
      case 'commerce': return 'Commerce';
      case 'entreprise': return 'Entreprise';
      case 'livreur': return 'Livreur';
      case 'artisan': return 'Artisan';
      default: return 'Client';
    }
  };

  const showDirections = () => {
    const url = `https://www.google.com/maps/dir/${pickupLocation.lat},${pickupLocation.lng}/${deliveryLocation.lat},${deliveryLocation.lng}`;
    window.open(url, '_blank');
  };

  const refreshMap = () => {
    if (mapRef.current) {
      const centerLat = (pickupLocation.lat + deliveryLocation.lat) / 2;
      const centerLng = (pickupLocation.lng + deliveryLocation.lng) / 2;
      
      const embedUrl = `https://www.google.com/maps/embed/v1/directions?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dOWTgkKSFRG-iI&origin=${pickupLocation.lat},${pickupLocation.lng}&destination=${deliveryLocation.lat},${deliveryLocation.lng}&mode=driving&language=fr&region=CI&zoom=13`;
      
      mapRef.current.innerHTML = `
        <iframe
          src="${embedUrl}"
          width="100%"
          height="100%"
          style="border:0; border-radius: 8px;"
          allowfullscreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Carte de suivi ONAPILA">
        </iframe>
      `;
    }
  };

  return (
    <div className={`bg-white rounded-xl shadow-lg overflow-hidden ${showFullScreen ? 'fixed inset-4 z-50' : ''}`}>
      {/* En-tête de la carte */}
      <div className="bg-gradient-to-r from-teal-600 to-blue-600 text-white p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">
              {isDriver ? 'Navigation GPS' : `Suivi de livraison - ${getUserTypeLabel(userType)}`}
            </h3>
            <p className="text-teal-100 text-sm">
              Commande #{deliveryId} • {orderDetails.orderType}
            </p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">{estimatedTime} min</div>
            <div className="text-teal-100 text-sm">{distance.toFixed(1)} km restants</div>
            <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium mt-1 ${getPriorityColor(orderDetails.priority)}`}>
              {orderDetails.priority === 'urgent' ? 'Urgent' : orderDetails.priority === 'express' ? 'Express' : 'Normal'}
            </span>
          </div>
        </div>
      </div>

      {/* Statut de livraison */}
      <div className="p-4 bg-gray-50 border-b">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className={`w-3 h-3 rounded-full ${getStatusColor(deliveryStatus)} animate-pulse`}></div>
            <span className="font-medium text-gray-900">{getStatusText(deliveryStatus)}</span>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={refreshMap}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-colors"
              title="Actualiser la carte"
            >
              <i className="ri-refresh-line text-lg"></i>
            </button>
            <button
              onClick={() => setShowFullScreen(!showFullScreen)}
              className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-200 rounded-lg transition-colors"
              title="Plein écran"
            >
              <i className={`ri-${showFullScreen ? 'close' : 'fullscreen'}-line text-lg`}></i>
            </button>
          </div>
        </div>

        {/* Informations des participants */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Informations client/expéditeur */}
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
              <i className="ri-user-line text-teal-600"></i>
            </div>
            <div>
              <p className="font-medium text-gray-900">
                {userType === 'livreur' ? 'Client' : 'Expéditeur'}
              </p>
              <p className="text-sm text-gray-600">{orderDetails.clientName}</p>
              <p className="text-xs text-gray-500">{pickupLocation.address}</p>
            </div>
          </div>

          {/* Informations livreur */}
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <i className="ri-truck-line text-blue-600"></i>
            </div>
            <div>
              <p className="font-medium text-gray-900">Livreur</p>
              <p className="text-sm text-gray-600">{orderDetails.driverName}</p>
              <p className="text-xs text-gray-500">Moto • Note: 4.8/5</p>
            </div>
          </div>
        </div>

        {/* Progression du trajet */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
            <span>Progression</span>
            <span>{Math.round((8.5 - distance) / 8.5 * 100)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-teal-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.round((8.5 - distance) / 8.5 * 100)}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Carte Interactive */}
      <div className="relative">
        <div
          ref={mapRef}
          style={{ 
            width: '100%', 
            height: showFullScreen ? '600px' : '400px',
            minHeight: '300px'
          }}
          className="bg-gray-100"
        >
          {/* Chargement initial */}
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Chargement de la carte...</p>
            </div>
          </div>
        </div>

        {/* Overlay avec position du livreur */}
        <div className="absolute top-4 left-4 bg-white rounded-lg shadow-lg p-3 max-w-xs">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
            <div>
              <p className="font-medium text-sm">Position livreur</p>
              <p className="text-xs text-gray-600">
                Mise à jour il y a {isTracking ? '3s' : '1 min'}
              </p>
            </div>
          </div>
        </div>

        {/* Informations de trajet */}
        <div className="absolute top-4 right-4 bg-white rounded-lg shadow-lg p-3">
          <div className="text-center">
            <div className="text-lg font-bold text-gray-900">{orderDetails.estimatedPrice}</div>
            <div className="text-xs text-gray-600">Prix de livraison</div>
          </div>
        </div>

        {/* Contrôles de navigation */}
        <div className="absolute bottom-4 right-4 flex flex-col space-y-2">
          <button
            onClick={showDirections}
            className="px-3 py-2 bg-blue-500 text-white rounded-lg shadow-lg hover:bg-blue-600 transition-colors"
            title="Ouvrir dans Google Maps"
          >
            <i className="ri-external-link-line"></i>
          </button>
          
          <button
            onClick={() => setIsTracking(!isTracking)}
            className={`px-4 py-2 rounded-lg font-medium text-sm shadow-lg transition-all whitespace-nowrap ${
              isTracking
                ? 'bg-red-500 hover:bg-red-600 text-white'
                : 'bg-teal-500 hover:bg-teal-600 text-white'
            }`}
          >
            {isTracking ? (
              <>
                <i className="ri-pause-line mr-2"></i>
                Arrêter suivi
              </>
            ) : (
              <>
                <i className="ri-play-line mr-2"></i>
                Démarrer suivi
              </>
            )}
          </button>
        </div>
      </div>

      {/* Informations détaillées */}
      <div className="p-4 bg-white">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-teal-600">{estimatedTime}</div>
            <div className="text-xs text-gray-600">Minutes restantes</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-blue-600">{distance.toFixed(1)}</div>
            <div className="text-xs text-gray-600">Km à parcourir</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">65</div>
            <div className="text-xs text-gray-600">Km/h vitesse moy.</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-orange-600">4.8</div>
            <div className="text-xs text-gray-600">Note livreur</div>
          </div>
        </div>
      </div>

      {/* Actions spécifiques selon le type d'utilisateur */}
      <div className="p-4 bg-gray-50 border-t">
        {/* Actions pour livreur */}
        {userType === 'livreur' && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <button className="flex items-center justify-center space-x-2 bg-green-500 hover:bg-green-600 text-white py-3 px-4 rounded-lg font-medium transition-colors whitespace-nowrap">
                <i className="ri-phone-line"></i>
                <span>Appeler client</span>
              </button>
              <button className="flex items-center justify-center space-x-2 bg-blue-500 hover:bg-blue-600 text-white py-3 px-4 rounded-lg font-medium transition-colors whitespace-nowrap">
                <i className="ri-message-3-line"></i>
                <span>Envoyer SMS</span>
              </button>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <button 
                onClick={() => setDeliveryStatus('collecte')}
                className="bg-yellow-500 hover:bg-yellow-600 text-white py-2 px-3 rounded-lg font-medium transition-colors text-sm whitespace-nowrap"
              >
                Collecté
              </button>
              <button 
                onClick={() => setDeliveryStatus('en_route')}
                className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-3 rounded-lg font-medium transition-colors text-sm whitespace-nowrap"
              >
                En route
              </button>
              <button 
                onClick={() => setDeliveryStatus('livre')}
                className="bg-green-500 hover:bg-green-600 text-white py-2 px-3 rounded-lg font-medium transition-colors text-sm whitespace-nowrap"
              >
                Livré
              </button>
            </div>
          </div>
        )}

        {/* Actions pour clients/entreprises/commerces/restaurants/artisans */}
        {userType !== 'livreur' && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <button className="flex items-center justify-center space-x-2 bg-teal-500 hover:bg-teal-600 text-white py-2 px-3 rounded-lg font-medium transition-colors text-sm whitespace-nowrap">
                <i className="ri-phone-line"></i>
                <span>Appeler livreur</span>
              </button>
              <button className="flex items-center justify-center space-x-2 bg-blue-500 hover:bg-blue-600 text-white py-2 px-3 rounded-lg font-medium transition-colors text-sm whitespace-nowrap">
                <i className="ri-message-3-line"></i>
                <span>Message</span>
              </button>
              <button className="flex items-center justify-center space-x-2 bg-orange-500 hover:bg-orange-600 text-white py-2 px-3 rounded-lg font-medium transition-colors text-sm whitespace-nowrap">
                <i className="ri-share-line"></i>
                <span>Partager</span>
              </button>
            </div>
            
            {/* Actions spécifiques selon le type */}
            {userType === 'restaurant' && (
              <div className="bg-orange-50 p-3 rounded-lg">
                <p className="text-sm text-orange-800 mb-2">
                  <i className="ri-temp-hot-line mr-1"></i>
                  Température maintenue: 65°C
                </p>
                <button className="w-full bg-orange-500 hover:bg-orange-600 text-white py-2 rounded-lg font-medium transition-colors text-sm whitespace-nowrap">
                  Confirmer préparation terminée
                </button>
              </div>
            )}

            {userType === 'commerce' && (
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm text-blue-800 mb-2">
                  <i className="ri-package-line mr-1"></i>
                  Colis prêt pour collecte
                </p>
                <button className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg font-medium transition-colors text-sm whitespace-nowrap">
                  Marquer comme prêt
                </button>
              </div>
            )}

            {userType === 'entreprise' && (
              <div className="bg-purple-50 p-3 rounded-lg">
                <p className="text-sm text-purple-800 mb-2">
                  <i className="ri-building-line mr-1"></i>
                  Livraison entreprise - Accès sécurisé
                </p>
                <button className="w-full bg-purple-500 hover:bg-purple-600 text-white py-2 rounded-lg font-medium transition-colors text-sm whitespace-nowrap">
                  Envoyer code d'accès
                </button>
              </div>
            )}

            {userType === 'artisan' && (
              <div className="bg-purple-50 p-3 rounded-lg">
                <p className="text-sm text-purple-800 mb-2">
                  <i className="ri-palette-line mr-1"></i>
                  Produit artisanal - Manipulation délicate
                </p>
                <button className="w-full bg-purple-500 hover:bg-purple-600 text-white py-2 rounded-lg font-medium transition-colors text-sm whitespace-nowrap">
                  Confirmer emballage sécurisé
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Historique des mises à jour */}
      <div className="p-4 bg-white border-t">
        <h4 className="font-medium text-gray-900 mb-3">Historique de livraison</h4>
        <div className="space-y-2">
          <div className="flex items-center space-x-3 text-sm">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-gray-600">14:30</span>
            <span className="text-gray-900">Commande confirmée par {getUserTypeLabel(userType)}</span>
          </div>
          <div className="flex items-center space-x-3 text-sm">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span className="text-gray-600">14:45</span>
            <span className="text-gray-900">Livreur assigné: {orderDetails.driverName}</span>
          </div>
          <div className="flex items-center space-x-3 text-sm">
            <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
            <span className="text-gray-600">15:00</span>
            <span className="text-gray-900">Collecte en cours</span>
          </div>
          <div className="flex items-center space-x-3 text-sm">
            <div className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></div>
            <span className="text-gray-600">15:15</span>
            <span className="text-gray-900">En route vers la destination</span>
          </div>
        </div>
      </div>
    </div>
  );
}
